<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'updateContact', 'showContact']);

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.code] = country;
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);
</script>

<template>
  <div :key="id" class="contact-card">
    <div class="contact-card-header">
      <div class="contact-info">
        <Avatar
          :name="name"
          :src="thumbnail"
          :size="48"
          :status="availabilityStatus"
          hide-offline-status
          rounded-full
          class="contact-avatar"
        />
        <div class="contact-details">
          <div class="contact-name-row">
            <span class="contact-name">{{ name }}</span>
            <span
              v-if="additionalAttributes?.companyName"
              class="contact-company"
            >
              <span class="i-ph-building-light size-4" />
              {{ additionalAttributes.companyName }}
            </span>
          </div>
          <div class="contact-meta">
            <span v-if="email" class="meta-item" :title="email">
              <span class="i-ph-envelope-simple-bold size-3.5" />
              {{ email }}
            </span>
            <span v-if="email && phoneNumber" class="meta-divider" />
            <span v-if="phoneNumber" class="meta-item">
              <span class="i-ph-phone-bold size-3.5" />
              {{ phoneNumber }}
            </span>
            <span v-if="phoneNumber && countryDetails" class="meta-divider" />
            <span v-if="countryDetails" class="meta-item">
              <Flag :country="countryDetails.countryCode" class="size-3.5" />
              {{ formattedLocation }}
            </span>
          </div>
        </div>
      </div>

      <div class="contact-actions">
        <button class="view-details-btn" @click="onClickViewDetails">
          <span class="i-ph-eye-bold size-4" />
          {{ t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS') }}
        </button>
        <button
          class="expand-btn"
          :class="{ expanded: isExpanded }"
          @click="onClickExpand"
        >
          <span class="i-ph-caret-down-bold size-4" />
        </button>
      </div>
    </div>

    <div class="contact-card-body" :class="{ expanded: isExpanded }">
      <div class="contact-form-wrapper">
        <ContactsForm
          ref="contactsFormRef"
          :contact-data="contactData"
          @update="handleFormUpdate"
        />
        <div class="form-actions">
          <button
            class="update-btn"
            :disabled="isUpdating || isFormInvalid"
            @click="handleUpdateContact"
          >
            <span v-if="isUpdating" class="loading-spinner-sm" />
            {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON') }}
          </button>
        </div>
      </div>
      <ContactDeleteSection
        :selected-contact="{
          id: props.id,
          name: props.name,
        }"
      />
    </div>
  </div>
</template>

<style scoped>
.contact-card {
  background: var(--wlab-bg-card);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.contact-card:hover {
  border-color: var(--wlab-border-default);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.contact-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  gap: 16px;
}

.contact-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.contact-avatar {
  flex-shrink: 0;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.contact-name-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--wlab-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-company {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--wlab-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--wlab-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-divider {
  width: 1px;
  height: 12px;
  background: var(--wlab-text-muted);
}

.contact-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.view-details-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  background: var(--wlab-bg-input);
  color: var(--wlab-text-secondary);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.view-details-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--wlab-text-primary);
}

.expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--wlab-bg-input);
  color: var(--wlab-text-secondary);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--wlab-text-primary);
}

.expand-btn.expanded {
  transform: rotate(180deg);
}

.contact-card-body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease;
  overflow: hidden;
}

.contact-card-body.expanded {
  grid-template-rows: 1fr;
}

.contact-form-wrapper {
  overflow: hidden;
  padding: 0 20px 20px;
  border-top: 1px solid var(--wlab-border-light);
}

.form-actions {
  margin-top: 20px;
}

.update-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: #fff;
  color: #000;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.update-btn:hover:not(:disabled) {
  background-color: #f0f0f0;
}

.update-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
